<template>
  <div
    ref="productGallery"
    class="product-gallery component-section critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <!-- Thumbnails -->
    <div
      v-if="(hasImages || hasVideos) && !isMobile"
      class="product-gallery__thumbs-wrapper"
    >
      <swiper
        class="product-gallery__thumbs"
        :modules="[Thumbs]"
        :slides-per-view="5"
        :space-between="20"
        :direction="'vertical'"
        :prevent-clicks="true"
        :prevent-interaction-on-transition="true"
        @swiper="setThumbsSwiper"
      >
        <swiper-slide
          v-for="(image, index) in images"
          class="product-gallery__thumb"
          :data-thumb-index="index"
          :id="`product-gallery-thumb-slide-${index}`"
          :key="index"
          tabindex="0"
        >
          <responsive-image
            block-class="product-gallery"
            :force-update="true"
            :padding="false"
            :image="image"
            min-max="100-200"
            :ratio="state === 'loading' || galleryImageRatio.thumbnail"
            sizes="50vw"
          />
        </swiper-slide>

        <swiper-slide
          v-for="(video, index) in videos"
          class="product-gallery__thumb"
          :data-thumb-index="images.length || index"
          :id="`product-gallery-thumb-slide-${index}`"
          :key="index"
          tabindex="0"
        >
          <responsive-image
            block-class="product-gallery"
            :force-update="true"
            :padding="false"
            :image="video.previewImage"
            min-max="100-200"
            :ratio="state === 'loading' || galleryImageRatio.thumbnail"
            sizes="50vw"
          />
          <icon-polygon-video-play />
        </swiper-slide>
      </swiper>

      <button
        v-if="hasThumbsArrowDown"
        :aria-label="'Next slide'"
        class="
          product-gallery__arrow-down
          swiper-navigation__arrow
          swiper-navigation__arrow-down
        "
        :class="{ 'reverse-direction': reverseThumbnailDirection }"
        @click="nextThumbSlide"
      >
        <icon-arrow-down />
      </button>
    </div>

    <!-- Main slider -->
    <div class="product-gallery__main">
      <template
        v-if="hasImages || hasVideos"
      >
        <swiper
          :breakpoints="breakpoints"
          :modules="modules"
          :navigation="navigation"
          :pagination="pagination"
          :slides-per-view="1"
          :space-between="0"
          :thumbs="{ swiper: thumbsSwiper }"
          :loop="true"
          @swiper="onSwiper"
        >
          <swiper-slide
            v-for="(image, index) in images"
            :id="`product-gallery-main-slide-${index}`"
            :key="index"
            class="product-gallery__slide"
          >
            <responsive-image
              :force-update="true"
              :image="image"
              min-max="300-1000"
              :ratio="state === 'loading' || responsiveRatio"
              sizes="50vw"
            />
          </swiper-slide>
          <swiper-slide
            v-for="(video, index) in videos"
            :id="`product-gallery-main-slide-${index}`"
            :key="index"
            class="product-gallery__slide"
          >
            <responsive-video
              :force-update="true"
              :lazyload="true"
              :video-src="video.src"
              :ratio="video.ratio"
              :type="`video/${video.type}`"
            />
          </swiper-slide>
        </swiper>

        <div
          v-if="hasPagination"
          class="product-gallery__actions"
        >
          <swiper-navigation
            :block-class="'product-gallery'"
            :section-id="sectionId"
          />
        </div>
      </template>

      <div
        v-if="!hasImages"
        class="product-gallery__placeholder"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product gallery
 * -----------------------------------------------------------------------------
 * Product gallery component.
 *
 * @param {String|Boolean} color - Currently active color.
 * @param {Array} images - Gallery images.
 * @param {Array} videos - Gallery videos.
 * @param {Boolean} mobile - Display mobile layout only.
 *
 */
import ResponsiveImage from '~global/images/responsive-image'
import ResponsiveVideo from '~global/responsive-video/responsive-video'
import SwiperNavigation from '~async/swiper-navigation/swiper-navigation'
import { ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y, Navigation, Pagination, Thumbs } from 'swiper'

import { values } from '~/config/breakpoints'
import { mapState } from 'vuex'
import IconArrowDown from '~icons/direction/arrow/down.svg'
import IconPolygonVideoPlay from '~icons/polygon/video-play.svg'

export default {
  name: 'ProductGallery',

  components: {
    ResponsiveImage,
    ResponsiveVideo,
    Swiper,
    SwiperSlide,
    SwiperNavigation,
    IconArrowDown,
    IconPolygonVideoPlay,
  },

  props: {
    sectionId: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    color: {
      type: [String, Boolean],
      required: false,
      default: false,
    },
    images: {
      type: [Array],
      required: true,
      default: () => [],
    },
    videos: {
      type: [Array],
      required: true,
      default: () => [],
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    template: {
      type: String,
      default: '',
    },
    galleryImageRatio: {
      type: Object,
      required: true,
      default: () => {},
    },
    state: {
      type: String,
      default: '',
    },
    selectedImageId: {
      type: Number,
      default: 0,
    },
  },

  setup(props) {
    const breakpoints = {}

    breakpoints[values.l] = {
      spaceBetween: 1,
    }

    const thumbsSwiper = ref(null)
    const setThumbsSwiper = (swiper) => {
      thumbsSwiper.value = swiper
    }

    return {
      breakpoints,
      modules: [
        A11y,
        Navigation,
        Pagination,
        Thumbs,
      ],
      navigation: {
        prevEl: `.swiper-button-prev-${props.sectionId}`,
        nextEl: `.swiper-button-next-${props.sectionId}`,
      },
      pagination: {
        clickable: true,
        el: `.swiper-pagination-${props.sectionId}`,
        type: 'bullets',
        dynamicBullets: false,
      },
      Thumbs,
      thumbsSwiper,
      setThumbsSwiper,
    }
  },

  data() {
    return {
      swiper: null,
      reverseThumbnailDirection: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobileState: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute has images.
     */
    hasImages() {
      return this.images?.length > 0
    },

    hasVideos() {
      return this.videos?.length > 0
    },

    hasThumbsArrowDown() {
      return this.images?.length > 5
    },

    hasPagination() {
      return this.images?.length > 1
    },


    isMobile() {
      return this.isMobileState
    },

    responsiveRatio() {
      return this.isMobileState ? this.galleryImageRatio.mobile : this.galleryImageRatio.desktop
    },
  },

  watch: {
    selectedImageId() {
      this.updateActiveSlide()
    },
  },

  mounted() {
  },

  methods: {

    /**
     * Save swiper instance.
     */
    onSwiper(swiper) {
      this.swiper = swiper

      document.documentElement.style.setProperty(
        '--product-gallery-height',
        `${this.swiper.height}px`,
      )

      if (this.thumbsSwiper) {
        this.thumbsSwiper.on('reachEnd', () => {
          this.reverseThumbnailDirection = true
        })

        this.thumbsSwiper.on('reachBeginning', () => {
          this.reverseThumbnailDirection = false
        })

        this.setThumbnailEventHandler()
      }

      this.updateActiveSlide()
    },

    setThumbnailEventHandler() {
      document.addEventListener('keydown', (e) => {
        if (e.defaultPrevented || e.key !== 'Enter') {
          return
        }

        const slideIndex = parseInt(e.target.dataset.thumbIndex) + 1

        if (!slideIndex) {
          return
        }

        this.swiper.slideTo(slideIndex)
      })
    },

    updateActiveSlide() {
      if (!this.swiper || typeof this.swiper.slideTo !== 'function') {
        return
      }

      const imageIndex = this.images.findIndex((image) => {
        return image.id === this.selectedImageId
      })

      if (imageIndex < 0 || !imageIndex) {
        return
      }

      this.swiper.slideTo(imageIndex)
    },

    nextThumbSlide() {
      if (!this.thumbsSwiper) {
        return
      }

      if (this.reverseThumbnailDirection) {
        this.thumbsSwiper.slideTo(0)
        return
      }

      const nextSetIndex = this.thumbsSwiper.visibleSlidesIndexes.slice(-1)[0] + 1
      this.thumbsSwiper.slideTo(nextSetIndex)
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery';
</style>
